import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { X, Loader2, Merge, Landmark } from "lucide-react";

const fmt = (v) => (Number(v) || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });

export const BulkMergeMunicipiosModal = ({ onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});       // clave -> bool (incluida)
  const [canon, setCanon] = useState({});    // clave -> nombre canónico
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/clientes360/merges/bulk-preview`);
        setData(r.data);
        const s = {}, c = {};
        r.data.propuestas.forEach((p) => { s[p.clave] = true; c[p.clave] = p.canonical; });
        setSel(s); setCanon(c);
      } finally { setLoading(false); }
    })();
  }, []);

  const nSel = data ? data.propuestas.filter((p) => sel[p.clave]).length : 0;

  const confirmar = async () => {
    if (!data || nSel === 0) return;
    setSubmitting(true);
    try {
      const merges = data.propuestas
        .filter((p) => sel[p.clave])
        .map((p) => ({ clave: p.clave, members: p.members, canonical: canon[p.clave] }));
      await axios.post(`${API}/clientes360/merges/bulk`, { merges });
      onDone?.();
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="bulk-municipios-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Landmark size={16} className="text-indigo-600" /> Fusión masiva de municipios</h2>
            <p className="text-[11px] text-gray-500">Revisa y confirma. Nada se fusiona sin tu visto bueno; cada fusión queda con traza y se recuerda el patrón: nuevos nombres del mismo municipio se atribuirán automáticamente.</p>
          </div>
          <button onClick={onClose} data-testid="bulk-municipios-close" className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 className="animate-spin" size={16} /> Detectando candidatos "Ayuntamiento de X ↔ X"…</div>}
          {data && data.total === 0 && <p className="text-sm text-gray-500 py-8 text-center">No hay pares de municipio pendientes de fusionar.</p>}
          {data && data.total > 0 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] text-gray-500">{data.total} par(es) detectado(s). Desmarca los que no quieras fusionar o cambia la cuenta canónica.</p>
                <div className="flex gap-2">
                  <button onClick={() => setSel(Object.fromEntries(data.propuestas.map((p) => [p.clave, true])))} data-testid="bulk-select-all" className="text-[10px] font-semibold px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Todas</button>
                  <button onClick={() => setSel({})} data-testid="bulk-select-none" className="text-[10px] font-semibold px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Ninguna</button>
                </div>
              </div>
              <div className="space-y-1.5">
                {data.propuestas.map((p) => (
                  <div key={p.clave} className={`rounded-lg border p-2.5 ${sel[p.clave] ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200 bg-gray-50/40"}`} data-testid={`bulk-row-${p.clave}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={!!sel[p.clave]} onChange={(e) => setSel((s) => ({ ...s, [p.clave]: e.target.checked }))} data-testid={`bulk-check-${p.clave}`} className="accent-emerald-600" />
                      <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                        {p.candidatos.map((c) => (
                          <span key={c.nombre} className={`inline-flex items-center gap-1 ${canon[p.clave] === c.nombre ? "font-bold text-emerald-800" : "text-gray-600"}`}>
                            {c.nombre}
                            <span className="text-[9px] text-gray-400">({c.n_ofertas} of · {c.n_oportunidades} op · {fmt(c.importe_total_mm)} M€)</span>
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[9px] text-gray-400">Canónica</span>
                        <select value={canon[p.clave]} onChange={(e) => setCanon((c) => ({ ...c, [p.clave]: e.target.value }))} data-testid={`bulk-canon-${p.clave}`} className="text-[10px] border border-gray-300 rounded px-1 py-0.5 bg-white max-w-[200px]">
                          {p.members.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {data && data.total > 0 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-[11px] text-gray-500">{nSel} seleccionada(s)</span>
            <button onClick={confirmar} disabled={nSel === 0 || submitting} data-testid="bulk-confirm-btn" className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Merge size={13} />} Fusionar {nSel} seleccionada(s)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkMergeMunicipiosModal;
